import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Box, InputAdornment, SelectChangeEvent, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CustomNoRowsOverlay from '../Table/CustomNoRowsOverlay';
import { IColumn } from './Columns';
import { ICoach } from '../../redux/coach/coachSlice';
import createPermissionCheckers from '../../permissions-validation/createPermissionCheckers';
import { UserPermissionType } from '../../types/commonTypes';
import SearchInput from '../Common/SearchInput';
import CustomDropdown from '../CustomDropdown/CustomDropdown';
import { CoachBrandTags, CoachBrandTagsTypes } from '../../types/coach';

export interface PaginationModelChangeProps {
  pageSize: number;
  page: number;
}

export interface CellClickProps {
  field: string;
  row: {
    id: string;
  };
}

interface CoachTableProps {
  handleOnPaginationModelChange: (_event: PaginationModelChangeProps) => void;
  rows: ICoach[];
  rowCount: number;
  loading: boolean;
  columns: IColumn[];
  handleOnChangeSearchTerm: (_v: string) => void;
  searchTerm: string;
  pageSize: number;
  page: number;
  coachesFilterhandler: (_event: SelectChangeEvent<unknown>) => void;
  selectedbrandTag?: string;
}

export default function CoachesTable({
  handleOnPaginationModelChange,
  rows,
  rowCount,
  loading,
  columns,
  handleOnChangeSearchTerm,
  searchTerm,
  pageSize,
  page,
  coachesFilterhandler,
  selectedbrandTag,
}: CoachTableProps) {
  const { t } = useTranslation();
  const permissionCheckers = createPermissionCheckers();
  const { checkPermissions } = permissionCheckers;
  const theme1 = useTheme();

  const getRowClassName = () => 'no-border';

  return (
    <Box
      height="100%"
      display="flex"
      flexDirection="column"
      bgcolor="#fff"
      borderRadius="10px"
      boxShadow={theme1.borderStyles.cardBorders}
    >
      <Box bgcolor="#fff" width="100%" display="flex" p="20px 24px" gap="5px">
        <Box width="90%" height="38px">
          <SearchInput
            type="text"
            placeholder={t('clientTable.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => handleOnChangeSearchTerm(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <img src="/assets/icons/magnifer.svg" alt="search" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box flex={1}>
          <CustomDropdown
            name={t('common.organization')}
            options={CoachBrandTags}
            value={selectedbrandTag || ''}
            onChange={coachesFilterhandler}
            placeholder={t('common.organization')}
            isTranslationProvided
          />
        </Box>
      </Box>
      <Box
        flexGrow="1"
        width="100%"
        height="calc(100vh - 264px)"
        sx={{
          '&::-webkit-scrollbar': {
            width: '0px',
            background: 'transparent',
          },
          scrollbarWidth: 'none',
          '-ms-overflow-style': 'none',
        }}
      >
        <DataGrid
          onPaginationModelChange={handleOnPaginationModelChange}
          rows={rows}
          rowCount={rowCount}
          paginationMode="server"
          columns={columns as GridColDef[]}
          getRowClassName={getRowClassName}
          columnVisibilityModel={{
            id: false,
            actions: checkPermissions(UserPermissionType.COACH_MODULE_DELETE_COACH),
          }}
          pageSizeOptions={[5, 10, 15]}
          disableRowSelectionOnClick
          columnHeaderHeight={44}
          loading={loading}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize,
                page,
              },
            },
          }}
          sx={{
            borderRight: 'none',
            borderLeft: 'none',
            borderBottom: 'none',
            borderColor: 'primary.light',
            '& .MuiDataGrid-cell': {
              borderBottom: 'none',
              paddingLeft: '30px',
              borderColor: 'primary.light',
              cursor: 'pointer',
            },
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              color: (theme) => theme.palette.primary.main,
              fontWeight: 500,
            },
            '& .MuiDataGrid-columnHeader:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-columnHeaderTitleContainer': {
              paddingLeft: '20px',
              border: 'none',
            },
            '& .MuiDataGrid-columnHeaders': {
              borderColor: 'primary.light',
            },
            '& .MuiDataGrid-footerContainer': {
              borderColor: 'primary.light',
            },
            '& .MuiTablePagination-root button': {
              mt: '4px',
              color: '#6CA3AC' /* Change text color */,
              '&:disabled': {
                opacity: 0.5,
              },
            },
            '& .MuiTablePagination-input': {
              color: 'primary.main',
              fontFamily: 'Satoshi',
              fontSize: '13px',
              fontWeight: 700,
              lineHeight: '18px',
              mt: '4px',
              ml: 0,
              pl: 0,
            },
            '& .MuiTablePagination-root .MuiTablePagination-selectLabel': {
              color: '#7CAEBE',
              fontFamily: 'Satoshi',
              fontSize: '13px',
              fontWeight: 400,
              lineHeight: '18px',
            },
            '& .MuiTablePagination-displayedRows': {
              color: 'primary.main',
              fontFamily: 'Satoshi',
              fontSize: '13px',
              fontWeight: 700,
              lineHeight: '18px',
              mt: '4px',
            },
            '& .MuiDataGrid-virtualScroller': {
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#f5f5f5',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#ccc',
                borderRadius: '20px',
                border: '3px solid #f5f5f5',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                backgroundColor: '#888',
              },
            },
          }}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
          }}
        />
      </Box>
    </Box>
  );
}
